import { useEffect, useRef, useState } from "react";
import { Clock, ArrowRight } from "lucide-react";
import { menuItems } from "@/mocks/content";
import MenuModal from "@/pages/home/components/MenuModal";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

interface MenuCardProps {
  item: (typeof menuItems)[0];
  index: number;
  inView: boolean;
  style?: React.CSSProperties;
  className?: string;
  onClick: (item: (typeof menuItems)[0]) => void;
}

function MenuCard({ item, index, inView, style, className, onClick }: MenuCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`relative overflow-hidden group cursor-pointer ${className ?? ""}`}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0) scale(1)" : "translateY(40px) scale(0.97)",
        transition: `all 0.7s ${0.1 + index * 0.12}s cubic-bezier(0.23, 1, 0.32, 1)`,
        boxShadow: hovered
          ? "0 30px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,163,115,0.2)"
          : "0 8px 30px rgba(0,0,0,0.4)",
        position: "relative",
        zIndex: hovered ? 50 : undefined,
        ...style,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onClick(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(item); }}
      aria-label={`${item.name} - 詳細を見る`}
    >
      {/* Image */}
      <div className="relative overflow-hidden" style={{ paddingBottom: "66.67%" }}>
        <img
          src={item.image}
          alt={item.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-top"
          style={{
            filter: hovered ? "brightness(0.65)" : "brightness(0.85)",
            transform: hovered ? "scale(1.08)" : "scale(1)",
            transition: "all 0.8s cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        />
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
          style={{
            opacity: hovered ? 1 : 0.6,
            transition: "opacity 0.4s",
          }}
        />
        {/* Duration badge */}
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1">
          <Clock size={10} className="text-[#D4A373]" />
          <span
            className="text-white/80 text-xs"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            {item.duration}
          </span>
        </div>
      </div>

      {/* Info */}
      <div
        className="p-4 md:p-5"
        style={{ background: "linear-gradient(to bottom, #1A1A1A, #141414)" }}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3
            className="text-white text-base font-medium leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif", letterSpacing: "0.05em" }}
          >
            {item.name}
          </h3>
          <span
            className="text-[#D4A373] text-sm font-medium whitespace-nowrap"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            {item.price}
          </span>
        </div>
        <p
          className="text-white/50 text-xs leading-relaxed line-clamp-2"
          style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
        >
          {item.description}
        </p>
        <div
          className="mt-3 flex items-center gap-1 text-[#D4A373]/70 text-xs"
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            opacity: hovered ? 1 : 0,
            transform: hovered ? "translateX(0)" : "translateX(-8px)",
            transition: "all 0.3s",
          }}
        >
          <span>詳細を見る</span>
          <ArrowRight size={11} />
        </div>
      </div>

      {/* Shadow Lift border */}
      <div
        className="absolute inset-0 border pointer-events-none"
        style={{
          borderColor: hovered ? "rgba(212,163,115,0.3)" : "rgba(255,255,255,0.05)",
          transition: "border-color 0.4s",
        }}
      />
    </div>
  );
}

export default function MenuSection() {
  const { ref, inView } = useInView(0.1);
  const [selectedItem, setSelectedItem] = useState<(typeof menuItems)[0] | null>(null);

  return (
    <section id="menu" className="relative py-20 md:py-32" style={{ background: "#111111" }}>
      <MenuModal item={selectedItem} onClose={() => setSelectedItem(null)} />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div
          ref={ref}
          className="mb-16 md:mb-20"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(30px)",
            transition: "all 0.8s cubic-bezier(0.23, 1, 0.32, 1)",
          }}
        >
          <div className="flex items-center gap-4 mb-4">
            <span className="w-12 h-px bg-[#D4A373]" />
            <span
              className="text-[#D4A373] text-xs tracking-[0.4em] uppercase"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              Treatment Menu
            </span>
          </div>
          <h2
            className="text-white"
            style={{
              fontFamily: "'Noto Serif JP', serif",
              fontSize: "clamp(1.8rem, 4vw, 3rem)",
              letterSpacing: "0.05em",
            }}
          >
            人気の施術メニュー
          </h2>
          <p
            className="mt-3 text-white/40 text-sm"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            お客様のニーズに合わせた多彩なメニューをご用意しています
          </p>
        </div>

        {/* Asymmetry Grid - Desktop */}
        <div className="hidden lg:block">
          {/* Row 1 */}
          <div className="relative flex items-start gap-5">
            {/* Card 1 - Large, left */}
            <div className="w-[40%] relative" style={{ zIndex: 10 }}>
              <MenuCard item={menuItems[0]} index={0} inView={inView} onClick={setSelectedItem} />
            </div>
            {/* Card 2 - Medium, center, offset down */}
            <div className="w-[35%] relative mt-14" style={{ zIndex: 20 }}>
              <MenuCard item={menuItems[1]} index={1} inView={inView} onClick={setSelectedItem} />
            </div>
            {/* Card 3 - Small, right, offset down more */}
            <div className="w-[25%] relative mt-28" style={{ zIndex: 10 }}>
              <MenuCard item={menuItems[2]} index={2} inView={inView} onClick={setSelectedItem} />
            </div>
          </div>

          {/* Row 2: offset cards, with top margin to create staggered feel */}
          <div className="relative flex items-start gap-5 mt-6">
            {/* Spacer */}
            <div className="w-[8%] shrink-0" />
            {/* Card 4 */}
            <div className="w-[33%] relative" style={{ zIndex: 30 }}>
              <MenuCard item={menuItems[3]} index={3} inView={inView} onClick={setSelectedItem} />
            </div>
            {/* Card 5 - Large, right */}
            <div className="w-[44%] relative mt-[-30px]" style={{ zIndex: 20 }}>
              <MenuCard item={menuItems[4]} index={4} inView={inView} onClick={setSelectedItem} />
            </div>
          </div>
        </div>

        {/* Mobile / Tablet Grid */}
        <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
          {menuItems.map((item, i) => (
            <MenuCard
              key={item.id}
              item={item}
              index={i}
              inView={inView}
              onClick={setSelectedItem}
              className={i === 4 ? "sm:col-span-2 sm:max-w-md sm:mx-auto w-full" : ""}
            />
          ))}
        </div>

        {/* Bottom note */}
        <div
          className="mt-12 md:mt-16 text-center"
          style={{
            opacity: inView ? 1 : 0,
            transition: "opacity 0.8s 0.8s",
          }}
        >
          <p
            className="text-white/30 text-xs tracking-widest"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            ※ 表示価格はすべて税込みです。施術時間は目安です。
          </p>
        </div>
      </div>
    </section>
  );
}
